import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Truck } from "lucide-react";

const LINES = ["Dead Engine.", "Heavy Load.", "We Roll. 24/7."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 700], [1, 0.35]);

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img
          src="/images/fleet-5.webp"
          alt="Flatlander Trucking Peterbilt semi at the yard"
          className="h-[115%] w-full object-cover brightness-110 contrast-105"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-ink/40" />
        <div className="grid-bg absolute inset-0" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-status-pill"
          className="mb-8 inline-flex items-center gap-3 border border-ember/40 bg-ink/70 px-4 py-2 backdrop-blur-md"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute h-full w-full animate-pulse-ring rounded-full bg-ember" />
            <span className="h-2.5 w-2.5 rounded-full bg-ember" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
            Dispatch Active — Rittman, OH
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          data-testid="hero-headline"
          className="font-display text-[17vw] font-black uppercase leading-[0.88] tracking-tight text-white sm:text-8xl lg:text-[9rem]"
        >
          {LINES.map((text, i) => (
            <span key={text} className="block overflow-hidden pb-1">
              <motion.span variants={line} className={`block ${i === 2 ? "text-ember" : ""}`}>
                {text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base text-slate-300 sm:text-lg"
        >
          Light &amp; heavy-duty towing, heavy truck roadside repair, lockouts and recovery —
          day, night, weekends, holidays. One call and a truck is rolling.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="tel:+13302014612"
            data-testid="hero-call-now-button"
            className="group flex items-center gap-3 bg-ember px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-200 hover:bg-emberdark hover:shadow-[0_0_36px_rgba(139,92,246,0.4)]"
          >
            <Phone className="h-5 w-5 transition-transform duration-200 group-hover:-rotate-12" />
            Call (330) 201-4612
          </a>
          <a
            href="#request"
            data-testid="hero-request-tow-button"
            className="flex items-center gap-3 border border-white/25 px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors duration-200 hover:border-ember hover:text-ember"
          >
            <Truck className="h-5 w-5" />
            Request a Tow
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          data-testid="hero-stats"
          className="mt-16 grid max-w-2xl grid-cols-3 divide-x divide-white/10 border-y border-white/10"
        >
          {[
            ["4.8/5", "Google Rating"],
            ["24/7", "Live Dispatch"],
            ["Light–Heavy", "Any Rig, Any Size"],
          ].map(([stat, label]) => (
            <div key={label} className="px-4 py-5 sm:px-6">
              <div className="font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">{stat}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
